import PropTypes from 'prop-types';
import React from 'react';

// Renders the common markdown subset the agent answers in. Builds React
// elements, never HTML, so no answer can inject markup into the admin panel.

const InlinePattern =
	/(`[^`]+`)|(\*\*[\s\S]+?\*\*)|(__[\s\S]+?__)|(~~[\s\S]+?~~)|(\*[^*\n]+\*)|(_[^_\n]+_)|(\[[^\]]*\]\([^)\s]+\))|(https?:\/\/[^\s<>()]+)/;

const LinkPattern = /^\[([^\]]*)\]\(([^)\s]+)\)$/;
const HeadingPattern = /^(#{1,6})\s+(.*)$/;
const UnorderedItemPattern = /^(\s*)[-*+]\s+(.*)$/;
const OrderedItemPattern = /^(\s*)\d+[.)]\s+(.*)$/;
const BlockquotePattern = /^>\s?(.*)$/;
const FencePattern = /^\s*```(.*)$/;
const RulePattern = /^\s*([-*_])(\s*\1){2,}\s*$/;
const TableDelimiterPattern = /^\s*\|?[\s:|-]*-[\s:|-]*\|?\s*$/;

const isSafeHref = (href) => /^(https?:|mailto:)/i.test(href);

const renderLink = (href, label, key) => {
	if (!isSafeHref(href)) {
		return <React.Fragment key={key}>{label}</React.Fragment>;
	}
	return (
		<a
			key={key}
			href={href}
			target="_blank"
			rel="noopener noreferrer"
			className="break-words text-emerald-700 underline">
			{label}
		</a>
	);
};

const renderToken = (token, key) => {
	if (token.startsWith('`')) {
		return (
			<code
				key={key}
				className="rounded bg-gray-100 px-1 py-[1px] font-mono text-[12px]">
				{token.slice(1, -1)}
			</code>
		);
	}
	if (token.startsWith('**') || token.startsWith('__')) {
		return (
			<strong key={key} className="font-semibold">
				{renderInline(token.slice(2, -2), key)}
			</strong>
		);
	}
	if (token.startsWith('~~')) {
		return <s key={key}>{renderInline(token.slice(2, -2), key)}</s>;
	}
	if (token.startsWith('*') || token.startsWith('_')) {
		return <em key={key}>{renderInline(token.slice(1, -1), key)}</em>;
	}
	const link = LinkPattern.exec(token);
	if (link) {
		return renderLink(link[2], link[1] || link[2], key);
	}
	return renderLink(token, token, key);
};

export const renderInline = (text, keyPrefix = 'i') => {
	const nodes = [];
	let rest = text;
	let index = 0;

	while (rest.length) {
		const match = InlinePattern.exec(rest);
		if (!match) {
			nodes.push(rest);
			break;
		}
		if (match.index > 0) {
			nodes.push(rest.slice(0, match.index));
		}
		nodes.push(renderToken(match[0], `${keyPrefix}-${index}`));
		rest = rest.slice(match.index + match[0].length);
		index += 1;
	}

	return nodes;
};

const renderLines = (lines, keyPrefix) =>
	lines.map((line, lineIndex) => (
		<React.Fragment key={`${keyPrefix}-l${lineIndex}`}>
			{lineIndex > 0 ? <br /> : null}
			{renderInline(line, `${keyPrefix}-l${lineIndex}`)}
		</React.Fragment>
	));

const splitRow = (line) =>
	line
		.trim()
		.replace(/^\|/, '')
		.replace(/\|$/, '')
		.split('|')
		.map((cell) => cell.trim());

const isTableRow = (line) => line.trim().startsWith('|');

const isBlockStart = (line) =>
	!line.trim() ||
	FencePattern.test(line) ||
	HeadingPattern.test(line) ||
	UnorderedItemPattern.test(line) ||
	OrderedItemPattern.test(line) ||
	BlockquotePattern.test(line) ||
	RulePattern.test(line) ||
	isTableRow(line);

// Deeper nesting is rare in answers, so indentation collapses to two levels.
const itemDepth = (indent) => (indent.length >= 2 ? 1 : 0);

const parseBlocks = (markdown) => {
	const lines = String(markdown).replace(/\r\n/g, '\n').split('\n');
	const blocks = [];
	let index = 0;

	while (index < lines.length) {
		const line = lines[index];

		if (!line.trim()) {
			index += 1;
			continue;
		}

		const fence = FencePattern.exec(line);
		if (fence) {
			const code = [];
			index += 1;
			while (index < lines.length && !FencePattern.test(lines[index])) {
				code.push(lines[index]);
				index += 1;
			}
			index += 1; // closing fence, or the end of the answer
			blocks.push({
				type: 'code',
				language: fence[1].trim(),
				code: code.join('\n'),
			});
			continue;
		}

		if (RulePattern.test(line)) {
			blocks.push({ type: 'rule' });
			index += 1;
			continue;
		}

		const heading = HeadingPattern.exec(line);
		if (heading) {
			blocks.push({
				type: 'heading',
				level: heading[1].length,
				text: heading[2],
			});
			index += 1;
			continue;
		}

		// A piped line is only a table when the next line is the |---| separator.
		if (
			isTableRow(line) &&
			index + 1 < lines.length &&
			lines[index + 1].includes('-') &&
			TableDelimiterPattern.test(lines[index + 1])
		) {
			const header = splitRow(line);
			index += 2;
			const rows = [];
			while (index < lines.length && isTableRow(lines[index])) {
				rows.push(splitRow(lines[index]));
				index += 1;
			}
			blocks.push({ type: 'table', header, rows });
			continue;
		}

		if (BlockquotePattern.test(line)) {
			const quoted = [];
			while (index < lines.length && BlockquotePattern.test(lines[index])) {
				quoted.push(BlockquotePattern.exec(lines[index])[1]);
				index += 1;
			}
			blocks.push({ type: 'quote', lines: quoted });
			continue;
		}

		const isOrdered = OrderedItemPattern.test(line);
		if (isOrdered || UnorderedItemPattern.test(line)) {
			const pattern = isOrdered ? OrderedItemPattern : UnorderedItemPattern;
			const items = [];
			while (index < lines.length) {
				const item = pattern.exec(lines[index]);
				if (!item) {
					break;
				}
				items.push({ depth: itemDepth(item[1]), text: item[2] });
				index += 1;
			}
			blocks.push({ type: 'list', ordered: isOrdered, items });
			continue;
		}

		// Take the first line unconditionally: re-testing it as a block start
		// would leave the cursor where it is.
		const paragraph = [lines[index]];
		index += 1;
		while (index < lines.length && !isBlockStart(lines[index])) {
			paragraph.push(lines[index]);
			index += 1;
		}
		blocks.push({ type: 'paragraph', lines: paragraph });
	}

	return blocks;
};

const listClass = (ordered) =>
	`my-1 space-y-1 pl-5 ${ordered ? 'list-decimal' : 'list-disc'}`;

const renderItems = (items, ordered, keyPrefix) => {
	const nodes = [];
	let index = 0;

	while (index < items.length) {
		const item = items[index];
		const itemKey = `${keyPrefix}-${index}`;
		const children = [];
		index += 1;
		while (index < items.length && items[index].depth > item.depth) {
			children.push(items[index]);
			index += 1;
		}
		nodes.push(
			<li key={itemKey}>
				{renderInline(item.text, itemKey)}
				{children.length
					? renderItems(children, ordered, `${itemKey}-c`)
					: null}
			</li>
		);
	}

	const ListTag = ordered ? 'ol' : 'ul';
	return <ListTag className={listClass(ordered)}>{nodes}</ListTag>;
};

const headingClass = (level) => {
	if (level <= 2) {
		return 'mt-2 mb-1 text-[15px] font-semibold';
	}
	if (level === 3) {
		return 'mt-2 mb-1 text-[14px] font-semibold';
	}
	return 'mt-2 mb-1 text-[13px] font-semibold';
};

const renderBlock = (block, key) => {
	switch (block.type) {
		case 'code':
			return (
				<pre
					key={key}
					className="my-2 overflow-x-auto rounded-lg bg-slate-900 p-3 text-[12px] leading-relaxed text-slate-100">
					<code>{block.code}</code>
				</pre>
			);
		case 'rule':
			return <hr key={key} className="my-3 border-gray-200" />;
		case 'heading':
			return (
				<p key={key} className={headingClass(block.level)}>
					{renderInline(block.text, key)}
				</p>
			);
		case 'table':
			return (
				<div key={key} className="my-2 overflow-x-auto">
					<table className="w-full border-collapse text-[12px]">
						<thead>
							<tr>
								{block.header.map((cell, cellIndex) => (
									<th
										key={`${key}-h${cellIndex}`}
										className="border border-gray-200 bg-gray-50 px-2 py-1 text-left font-semibold">
										{renderInline(cell, `${key}-h${cellIndex}`)}
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{block.rows.map((row, rowIndex) => (
								<tr key={`${key}-r${rowIndex}`}>
									{row.map((cell, cellIndex) => (
										<td
											key={`${key}-r${rowIndex}c${cellIndex}`}
											className="border border-gray-200 px-2 py-1 align-top">
											{renderInline(
												cell,
												`${key}-r${rowIndex}c${cellIndex}`
											)}
										</td>
									))}
								</tr>
							))}
						</tbody>
					</table>
				</div>
			);
		case 'quote':
			return (
				<blockquote
					key={key}
					className="my-2 border-l-2 border-gray-300 pl-3 italic text-gray-600">
					{renderLines(block.lines, key)}
				</blockquote>
			);
		case 'list':
			return (
				<React.Fragment key={key}>
					{renderItems(block.items, block.ordered, key)}
				</React.Fragment>
			);
		default:
			return (
				<p key={key} className="my-1 leading-relaxed">
					{renderLines(block.lines, key)}
				</p>
			);
	}
};

ChatMarkdown.propTypes = {
	content: PropTypes.string,
};

export default function ChatMarkdown({ content }) {
	if (!content) {
		return null;
	}
	return (
		<div className="break-words text-[13px]">
			{parseBlocks(content).map((block, index) =>
				renderBlock(block, `b${index}`)
			)}
		</div>
	);
}
